import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { ArrowRight, User, Heart, Globe, Briefcase, Quote, Play } from "lucide-react";

const RIPPLE_FULL = "https://customer-assets.emergentagent.com/job_jci-content-platform/artifacts/bkxs4swk_JCI_Asset-Ripples_a01.png";
const RIPPLE_LIGHT = "https://customer-assets.emergentagent.com/job_jci-content-platform/artifacts/xmrja44a_Ripples.png";

const ICON_MAP = { User, Heart, Globe, Briefcase };

function useInView(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return visible;
}

function Section({ children, className = "", id }) {
  const ref = useRef(null);
  const vis = useInView(ref);
  return (
    <section ref={ref} id={id} className={`section-animate ${vis ? "visible" : ""} ${className}`}>
      {children}
    </section>
  );
}

function Counter({ end, suffix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const vis = useInView(ref);
  useEffect(() => {
    if (!vis) return;
    const num = parseInt(end);
    const dur = 2000;
    const step = Math.ceil(num / (dur / 16));
    let cur = 0;
    const timer = setInterval(() => { cur += step; if (cur >= num) { cur = num; clearInterval(timer); } setCount(cur); }, 16);
    return () => clearInterval(timer);
  }, [vis, end]);
  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

export default function HomePage() {
  const [sliders, setSliders] = useState([]);
  const [about, setAbout] = useState(null);
  usePageTitle("Home");
  const [impacts, setImpacts] = useState([]);
  const [president, setPresident] = useState(null);
  const [areas, setAreas] = useState([]);
  const [alumni, setAlumni] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [news, setNews] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [carouselApi, setCarouselApi] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [sectionSettings, setSectionSettings] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/content/sliders"), api.get("/pages/by-slug/homepage-about"),
      api.get("/content/impact_numbers"), api.get("/content/president_message"),
      api.get("/content/areas_of_opportunity"), api.get("/content/alumni"),
      api.get("/content/testimonials"), api.get("/content/news"),
      api.get("/homepage-settings"),
    ]).then(([s, a, i, p, ar, al, t, n, hs]) => {
      setSliders(s.data.filter(x => x.is_active !== false));
      setAbout(a.data);
      setImpacts(i.data);
      setPresident(p.data?.[0] || p.data);
      setAreas(ar.data);
      setAlumni(al.data);
      setTestimonials(t.data);
      setNews(n.data.filter(x => x.is_published !== false && x.show_on_home !== false));
      // Build settings map
      const settingsMap = {};
      (hs.data || []).forEach(s => { settingsMap[s.section_key] = s; });
      setSectionSettings(settingsMap);
    }).catch(() => {});
  }, []);

  const onSlideSelect = useCallback(() => {
    if (!carouselApi) return;
    setCurrentSlide(carouselApi.selectedScrollSnap());
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) return;
    carouselApi.on("select", onSlideSelect);
    return () => carouselApi.off("select", onSlideSelect);
  }, [carouselApi, onSlideSelect]);

  const autoplayPlugin = useRef(Autoplay({ delay: 5000, stopOnInteraction: false }));
  const alumniAutoplay = useRef(Autoplay({ delay: 3000, stopOnInteraction: false }));

  // Parse about images - support collage_images array or single image_url
  const aboutImages = about?.collage_images?.length > 0 ? about.collage_images : (about?.image_url ? [about.image_url] : []);

  const isVisible = (key) => {
    const s = sectionSettings[key];
    return !s || s.visible !== false;
  };

  const getSectionTitle = (key, fallback) => {
    const s = sectionSettings[key];
    return s?.section_title || fallback;
  };

  const getSectionSubtitle = (key, fallback) => {
    const s = sectionSettings[key];
    return s?.section_subtitle || fallback;
  };

  return (
    <div data-testid="home-page">
      {/* Hero Slider */}
      {sliders.length > 0 && isVisible("hero_slider") && (
        <div className="relative" data-testid="hero-slider">
          <Carousel opts={{ loop: true }} plugins={[autoplayPlugin.current]} setApi={setCarouselApi} className="w-full">
            <CarouselContent className="-ml-0">
              {sliders.map((slide, i) => (
                <CarouselItem key={slide.id} className="pl-0">
                  <div className="relative h-[70vh] md:h-[85vh] w-full">
                    <img src={slide.image_url} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="hero-overlay absolute inset-0" />
                    <div className="absolute inset-0 flex items-center">
                      <div className="container mx-auto px-6 lg:px-16 max-w-7xl">
                        <div className="max-w-2xl">
                          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 tracking-tight" data-testid={`slider-title-${i}`}>
                            {slide.title}
                          </h1>
                          <p className="text-base md:text-lg text-white/70 mb-8 leading-relaxed max-w-lg">{slide.subtitle}</p>
                          {slide.cta_text && (
                            <Link to={slide.cta_link || "#"}>
                              <Button data-testid={`slider-cta-${i}`} className="bg-jci-blue hover:bg-jci-navy text-white px-8 py-3 text-sm font-semibold tracking-wide shadow-lg shadow-blue-500/30 transition-all hover:shadow-blue-500/50">
                                {slide.cta_text} <ArrowRight className="w-4 h-4 ml-2" />
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-10">
            {sliders.map((_, i) => (
              <button key={i} onClick={() => carouselApi?.scrollTo(i)} data-testid={`slide-dot-${i}`}
                className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentSlide ? "bg-jci-blue w-8" : "bg-white/40 hover:bg-white/60"}`} />
            ))}
          </div>
        </div>
      )}

      {/* About Section - Image Collage Design */}
      {about && isVisible("about") && (
        <Section id="about" className="py-24 bg-white relative overflow-hidden">
          <img src={RIPPLE_LIGHT} alt="" className="ripple-bg -right-40 -top-40 w-[600px]" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-jci-blue mb-4 block">{getSectionSubtitle("about", "Who We Are")}</span>
                <h2 className="text-3xl sm:text-4xl font-bold text-jci-black mb-6 tracking-tight" data-testid="about-title">{about.title}</h2>
                <p className="text-slate-600 leading-relaxed text-base">{about.content}</p>
                <Link to="/page/about-jci-bangladesh">
                  <Button data-testid="about-learn-more" variant="outline" className="mt-8 border-jci-blue text-jci-blue hover:bg-jci-blue hover:text-white">
                    Learn More <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              {/* Image Collage */}
              <div className="relative" data-testid="about-collage">
                {aboutImages.length === 1 && (
                  <div className="relative">
                    <div className="absolute -inset-4 bg-jci-blue/10 rounded-2xl -rotate-3" />
                    <img src={aboutImages[0]} alt={about.title} className="relative rounded-2xl shadow-xl w-full h-[400px] object-cover" />
                  </div>
                )}
                {aboutImages.length === 2 && (
                  <div className="grid grid-cols-2 gap-4">
                    <img src={aboutImages[0]} alt="" className="rounded-2xl shadow-lg w-full h-[300px] object-cover" />
                    <img src={aboutImages[1]} alt="" className="rounded-2xl shadow-lg w-full h-[300px] object-cover mt-8" />
                  </div>
                )}
                {aboutImages.length >= 3 && (
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-7">
                      <img src={aboutImages[0]} alt="" className="rounded-2xl shadow-lg w-full h-[350px] object-cover" />
                    </div>
                    <div className="col-span-5 flex flex-col gap-3">
                      <img src={aboutImages[1]} alt="" className="rounded-2xl shadow-lg w-full h-[170px] object-cover" />
                      <img src={aboutImages[2]} alt="" className="rounded-2xl shadow-lg w-full h-[170px] object-cover" />
                    </div>
                    {aboutImages.length >= 4 && (
                      <div className="col-span-12 grid grid-cols-3 gap-3">
                        {aboutImages.slice(3, 6).map((img, idx) => (
                          <img key={idx} src={img} alt="" className="rounded-xl shadow-md w-full h-[120px] object-cover" />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* Impact Numbers */}
      {impacts.length > 0 && isVisible("impact_numbers") && (
        <Section id="impacts" className="py-24 bg-jci-black relative overflow-hidden">
          <img src={RIPPLE_FULL} alt="" className="ripple-bg left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] opacity-[0.04]" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
            <div className="text-center mb-16">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-jci-teal mb-3 block">{getSectionSubtitle("impact_numbers", "Our Impact")}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">{getSectionTitle("impact_numbers", "Key Highlights")}</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center" data-testid="impact-numbers">
              {impacts.map((item, i) => (
                <div key={item.id} className="p-6">
                  <div className="text-5xl md:text-6xl font-extrabold text-jci-blue impact-number mb-3" data-testid={`impact-number-${i}`}>
                    <Counter end={item.number} suffix={item.suffix} />
                  </div>
                  <div className="text-xs font-bold uppercase tracking-[0.15em] text-white/60">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* President Message with Video Embed */}
      {president && isVisible("president_message") && (
        <Section id="president" className="py-24 bg-white relative overflow-hidden">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
              <div className="lg:col-span-2">
                <div className="relative">
                  <div className="absolute -inset-3 bg-jci-teal/10 rounded-2xl rotate-2" />
                  {president.photo_url && (
                    <img src={president.photo_url} alt={president.name} className="relative rounded-2xl shadow-xl w-full h-[450px] object-cover object-top" data-testid="president-photo" />
                  )}
                </div>
              </div>
              <div className="lg:col-span-3" data-testid="president-message-section">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-jci-blue mb-4 block">{getSectionSubtitle("president_message", "Message from")}</span>
                <h2 className="text-3xl sm:text-4xl font-bold text-jci-black mb-2 tracking-tight">{president.name}</h2>
                <p className="text-sm font-semibold text-jci-blue mb-6">{president.title}</p>
                <div className="border-l-4 border-jci-blue pl-6 mb-8">
                  <p className="text-slate-600 leading-relaxed text-base font-serif italic">{president.message}</p>
                </div>
                {/* YouTube Video Embed */}
                {president.video_url && getYouTubeId(president.video_url) && (
                  <div data-testid="president-video">
                    {showVideo ? (
                      <div className="relative rounded-xl overflow-hidden shadow-lg aspect-video">
                        <iframe
                          src={`https://www.youtube.com/embed/${getYouTubeId(president.video_url)}?autoplay=1`}
                          title="President's Message Video"
                          className="absolute inset-0 w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : (
                      <button onClick={() => setShowVideo(true)} className="group relative rounded-xl overflow-hidden shadow-lg w-full aspect-video bg-jci-black/5" data-testid="president-video-play">
                        <img src={`https://img.youtube.com/vi/${getYouTubeId(president.video_url)}/hqdefault.jpg`} alt="Video thumbnail" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-jci-black/30 flex items-center justify-center group-hover:bg-jci-black/40 transition-colors">
                          <div className="w-16 h-16 rounded-full bg-jci-blue flex items-center justify-center shadow-lg shadow-jci-blue/30 group-hover:scale-110 transition-transform">
                            <Play className="w-7 h-7 text-white ml-1" />
                          </div>
                        </div>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Section>
      )}

      {/* Areas of Opportunity - icons center aligned */}
      {areas.length > 0 && isVisible("areas_of_opportunity") && (
        <Section id="areas" className="py-24 bg-slate-50 relative overflow-hidden">
          <img src={RIPPLE_LIGHT} alt="" className="ripple-bg -left-40 -bottom-40 w-[500px]" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
            <div className="text-center mb-16">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-jci-blue mb-3 block">{getSectionSubtitle("areas_of_opportunity", "What We Do")}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-jci-black tracking-tight">{getSectionTitle("areas_of_opportunity", "Areas of Opportunity")}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="areas-grid">
              {areas.map((area, i) => {
                const IconComp = ICON_MAP[area.icon] || Globe;
                return (
                  <Link to={area.link || "#"} key={area.id}>
                    <Card className="card-hover border-0 shadow-md bg-white h-full" data-testid={`area-card-${i}`}>
                      <CardContent className="p-8 flex flex-col items-center text-center">
                        <div className="w-14 h-14 rounded-xl bg-jci-blue/10 flex items-center justify-center mb-6">
                          <IconComp className="w-7 h-7 text-jci-blue" />
                        </div>
                        <h3 className="text-lg font-bold text-jci-black mb-3">{area.title}</h3>
                        <p className="text-sm text-slate-500 leading-relaxed">{area.description}</p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </Section>
      )}

      {/* Remarkable Alumni - Dynamic Slider */}
      {alumni.length > 0 && isVisible("alumni") && (
        <Section id="alumni" className="py-24 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="text-center mb-16">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-jci-blue mb-3 block">{getSectionSubtitle("alumni", "Our Pride")}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-jci-black tracking-tight">{getSectionTitle("alumni", "Remarkable Alumni")}</h2>
            </div>
            <div className="relative px-12" data-testid="alumni-slider">
              <Carousel opts={{ loop: true, align: "start" }} plugins={[alumniAutoplay.current]} className="w-full">
                <CarouselContent className="-ml-4">
                  {alumni.map((person, i) => (
                    <CarouselItem key={person.id} className="pl-4 basis-full sm:basis-1/2 md:basis-1/3 lg:basis-1/4">
                      <div className="text-center group" data-testid={`alumni-card-${i}`}>
                        <div className="relative mb-5 overflow-hidden rounded-2xl">
                          <img src={person.photo_url} alt={person.name} className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105" />
                          <div className="absolute inset-0 bg-gradient-to-t from-jci-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <h3 className="font-bold text-jci-black text-base">{person.name}</h3>
                        <p className="text-xs text-slate-500 mt-1">{person.designation}</p>
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="border-jci-blue text-jci-blue hover:bg-jci-blue hover:text-white" />
                <CarouselNext className="border-jci-blue text-jci-blue hover:bg-jci-blue hover:text-white" />
              </Carousel>
            </div>
          </div>
        </Section>
      )}

      {/* Testimonials - Scaled up ripple in corner */}
      {testimonials.length > 0 && isVisible("testimonials") && (
        <Section id="testimonials" className="py-24 bg-jci-black relative overflow-hidden">
          <img src={RIPPLE_FULL} alt="" className="absolute -right-[300px] -top-[300px] w-[900px] h-[900px] opacity-[0.06] pointer-events-none animate-spin-slow" />
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
            <div className="text-center mb-16">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-jci-teal mb-3 block">{getSectionSubtitle("testimonials", "Voices of Impact")}</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">{getSectionTitle("testimonials", "What Industry Leaders Say")}</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="testimonials-grid">
              {testimonials.map((t, i) => (
                <Card key={t.id} className="bg-white/5 border-white/10 backdrop-blur-sm" data-testid={`testimonial-card-${i}`}>
                  <CardContent className="p-8">
                    <Quote className="w-8 h-8 text-jci-blue/30 mb-4" />
                    <p className="text-white/70 font-serif italic leading-relaxed text-sm mb-6">{t.quote}</p>
                    <div className="border-t border-white/10 pt-4">
                      <p className="font-bold text-white text-sm">{t.name}</p>
                      <p className="text-xs text-jci-teal">{t.title}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Latest News - Clickable to full news page */}
      {news.length > 0 && isVisible("news") && (
        <Section id="news" className="py-24 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="flex items-end justify-between mb-16">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-jci-blue mb-3 block">{getSectionSubtitle("news", "Stay Updated")}</span>
                <h2 className="text-3xl sm:text-4xl font-bold text-jci-black tracking-tight">{getSectionTitle("news", "Latest News")}</h2>
              </div>
              <Link to="/news-press">
                <Button variant="outline" className="border-jci-blue text-jci-blue hover:bg-jci-blue hover:text-white hidden md:flex">
                  View All <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="news-grid">
              {news.slice(0, 3).map((item, i) => (
                <Link to={`/news/${item.id}`} key={item.id} data-testid={`news-link-${i}`}>
                  <Card className="card-hover border-0 shadow-md overflow-hidden group cursor-pointer h-full" data-testid={`news-card-${i}`}>
                    <div className="overflow-hidden h-48">
                      <img src={item.image_url} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <CardContent className="p-6">
                      <p className="text-xs text-jci-blue font-semibold mb-2">{item.published_date}</p>
                      <h3 className="font-bold text-jci-black text-base mb-2 line-clamp-2 group-hover:text-jci-blue transition-colors">{item.title}</h3>
                      <p className="text-sm text-slate-500 line-clamp-2">{item.excerpt}</p>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-jci-blue mt-3 group-hover:gap-2 transition-all">
                        Read More <ArrowRight className="w-3 h-3" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}
